// webKnowledge — grows the workspace knowledge base from creative analysis.
//
// Both "Send to Brain" (script + frames + brain scan) and "Generate variation
// brief" (drop-off + script rewrite) feed what they learn back here. Each call
// appends a compact, citable source to knowledge_chunks so future briefs and
// chat answers can reference real, product-specific learnings — the KB
// compounds every time a creative is analyzed.

import { apiFetch } from "./apiClient";

const pct = (v) => `${Math.round((Number(v) || 0) * 100)}%`;

const perfLines = (node) => {
  const lines = [];
  if (node?.spend != null) lines.push(`- Spend: $${Number(node.spend).toLocaleString()}`);
  if (node?.roas != null) lines.push(`- ROAS: ${Number(node.roas).toFixed(2)}`);
  if (node?.ctr != null) lines.push(`- CTR: ${Number(node.ctr).toFixed(2)}%`);
  if (node?.hookRate != null) lines.push(`- Hook rate: ${pct(node.hookRate)}`);
  if (node?.confidence != null) lines.push(`- Confidence: ${pct(node.confidence)}`);
  return lines.join("\n");
};

const retentionLines = (retention) => {
  if (!retention) return "";
  const curve = (retention.curve || [])
    .map((p) => `${p.label} → ${pct(p.pct)}`)
    .join(", ");
  const parts = [];
  if (curve) parts.push(`- Retention curve: ${curve}`);
  if (retention.dropOff) {
    parts.push(
      `- Sharpest drop-off: ${retention.dropOff.from} → ${retention.dropOff.to} (lost ${pct(
        retention.dropOff.dropPct,
      )} of viewers)`,
    );
  }
  if (retention.completionRate != null) {
    parts.push(`- Completion: ${pct(retention.completionRate)}`);
  }
  return parts.join("\n");
};

/**
 * Build a single KB source (markdown) from a creative learning. Returns
 * { title, locator, body } ready for /api/knowledge/ingest.
 */
export const buildCreativeSource = ({ node, kind, learning = {} }) => {
  const label = node?.label || "Untitled creative";
  const id = node?._meta?.ad_id || node?._meta?.creative_id || node?.id || "";
  const title = `Creative learning · ${label}`;
  const locator = id ? `${kind}:${id}` : kind;

  const sections = [
    `# ${label} — ${kind === "brief" ? "variation brief learnings" : "brain scan learnings"}`,
    id ? `Source creative: ${id}` : "",
    "",
    "## Performance",
    perfLines(node) || "- (no performance metrics available)",
  ];

  const ret = retentionLines(learning.retention || node?.retention);
  if (ret) {
    sections.push("", "## Watch-through / drop-off", ret);
  }

  if (learning.transcript) {
    sections.push(
      "",
      "## Script (as delivered)",
      String(learning.transcript).slice(0, 2400),
    );
  }

  if (learning.whatWorked?.length) {
    sections.push(
      "",
      "## What worked",
      learning.whatWorked.map((w) => `- ${w}`).join("\n"),
    );
  }

  if (learning.dropOffDiagnosis) {
    sections.push("", "## Why viewers dropped", learning.dropOffDiagnosis);
  }

  if (learning.scriptAdjustments?.length) {
    sections.push(
      "",
      "## Script adjustments recommended",
      learning.scriptAdjustments.map((s, i) => `${i + 1}. ${s}`).join("\n"),
    );
  }

  if (learning.brainInsight) {
    sections.push("", "## Neuro / attention insight", learning.brainInsight);
  }

  if (learning.thesis) {
    sections.push("", "## Thesis", learning.thesis);
  }

  return {
    title,
    locator,
    body: sections.filter((s) => s !== undefined && s !== null).join("\n").trim(),
  };
};

/**
 * Append a creative learning to the workspace KB. No-op (returns null) when
 * there's no workspace. Best-effort: failures are swallowed so they never
 * block the primary analysis flow.
 */
export const ingestCreativeLearning = async ({ workspaceId, node, kind, learning }) => {
  if (!workspaceId || !node) return null;
  const source = buildCreativeSource({ node, kind, learning });
  if (!source.body) return null;
  try {
    const data = await apiFetch(
      `/api/knowledge/ingest?workspace_id=${encodeURIComponent(workspaceId)}`,
      {
        method: "POST",
        body: {
          workspace_id: workspaceId,
          mode: "append",
          sources: [source],
        },
      },
    );
    return { inserted: data?.inserted || 0, source };
  } catch (error) {
    console.warn("webKnowledge.ingestCreativeLearning failed", error);
    return null;
  }
};
